/*
 * Live search dropdown (desktop header + mobile menu).
 *
 * v2: Local search index instead of hitting Shopify /search per keystroke.
 *
 * Why: Shopify's storefront search only prefix-matches against certain fields
 * (titles etc.) - not descriptions. So "carolina" (whole word in description)
 * matched but "caroli" (prefix) did not. With ~65 products it's cheaper and
 * far more reliable to fetch the whole catalog once as JSON (rendered by
 * sections/search-index.liquid via the Section Rendering API), cache it in
 * sessionStorage, and match locally:
 *   - prefix matching on EVERY word ("caroli" -> "Carolina", "herr" -> "Herrera")
 *   - the custom.inspired metafield is fully searchable
 *   - results from the first letter, instantly, no rate limiting
 *
 * Fallback: if the index can't be loaded, the old server-side section search
 * (predictive-search-mini) is used instead.
 */

const PS_INDEX_SECTION_ID = "search-index";
const PS_FALLBACK_SECTION_ID = "predictive-search-mini";
const PS_MIN_CHARS = 1;
const PS_DEBOUNCE_MS = 100;
const PS_MAX_ROWS = 8;
const PS_CACHE_KEY = "ps-index-v1";
const PS_CACHE_TTL_MS = 30 * 60 * 1000; // 30 min

// ---------------------------------------------------------------------------
// Shared index (one per page, shared by all <search-form> instances)
// ---------------------------------------------------------------------------

let psIndexPromise = null;

function psRoot() {
  return (window.Shopify && Shopify.routes && Shopify.routes.root) || "/";
}

function psNormalize(str) {
  return (str || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, ""); // é -> e, è -> e osv.
}

function psLoadIndex() {
  if (psIndexPromise) return psIndexPromise;

  // sessionStorage-cache
  try {
    const cached = JSON.parse(sessionStorage.getItem(PS_CACHE_KEY));
    if (cached && Date.now() - cached.time < PS_CACHE_TTL_MS && Array.isArray(cached.products)) {
      psIndexPromise = Promise.resolve(cached.products);
      return psIndexPromise;
    }
  } catch (e) { /* korrupt cache - hämta om */ }

  const fetchPage = (page) =>
    fetch(`${psRoot()}?section_id=${PS_INDEX_SECTION_ID}&page=${page}`)
      .then((r) => {
        if (!r.ok) throw new Error("HTTP " + r.status);
        return r.text();
      })
      .then((text) => {
        const doc = new DOMParser().parseFromString(text, "text/html");
        const el = doc.getElementById("search-index-data");
        if (!el) throw new Error("index-section-missing");
        return JSON.parse(el.textContent);
      });

  psIndexPromise = fetchPage(1)
    .then((first) => {
      const extra = [];
      for (let p = 2; p <= (first.pages || 1); p++) extra.push(fetchPage(p));
      return Promise.all(extra).then((rest) => {
        let products = first.products;
        rest.forEach((r) => (products = products.concat(r.products)));
        return products;
      });
    })
    .then((products) => {
      // Förbered normaliserade ordlistor en gång
      products.forEach((p) => {
        p._tw = psNormalize(p.t).split(/[^a-z0-9]+/).filter(Boolean); // titelord
        p._sw = psNormalize(p.s).split(/[^a-z0-9]+/).filter(Boolean); // inspired-ord
      });
      try {
        sessionStorage.setItem(PS_CACHE_KEY, JSON.stringify({ time: Date.now(), products }));
      } catch (e) { /* full storage - strunta i cache */ }
      return products;
    })
    .catch((err) => {
      psIndexPromise = null; // tillåt nytt försök senare
      throw err;
    });

  return psIndexPromise;
}

// Varje sökterm måste prefix-matcha något ord i titel eller inspired-text.
// Titelmatchningar väger tyngre i sorteringen.
function psMatch(products, query) {
  const terms = psNormalize(query).split(/\s+/).filter(Boolean);
  if (!terms.length) return [];

  const scored = [];
  for (const p of products) {
    let score = 0;
    let ok = true;
    for (const term of terms) {
      const inTitle = p._tw.some((w) => w.startsWith(term));
      const inInspired = p._sw.some((w) => w.startsWith(term));
      if (inTitle) score += 2;
      else if (inInspired) score += 1;
      else { ok = false; break; }
    }
    if (ok) scored.push({ p, score });
  }
  scored.sort((a, b) => b.score - a.score || a.p.t.localeCompare(b.p.t, "sv"));
  return scored.slice(0, PS_MAX_ROWS).map((s) => s.p);
}

function psEscapeHtml(str) {
  return (str || "").replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c])
  );
}

function psRenderRows(products, query) {
  if (!products.length) {
    return '<div class="custom-live-search__empty">Inga träffar</div>';
  }
  const rows = products
    .map((p) => {
      const img = p.i
        ? `<img src="${psEscapeHtml(p.i)}" width="45" height="45" loading="lazy" alt="${psEscapeHtml(p.t)}">`
        : "";
      const sub = p.s
        ? `<span class="custom-live-search__subtitle">Inspirerad av <strong>${psEscapeHtml(String(p.s).replace(' - ', ' – '))}</strong></span>`
        : "";
      return (
        `<li class="custom-live-search__li" role="option" aria-selected="false">` +
        `<a class="custom-live-search__item" href="${psEscapeHtml(p.u)}">` +
        `<span class="custom-live-search__thumb">${img}</span>` +
        `<span class="custom-live-search__text">` +
        `<span class="custom-live-search__title">${psEscapeHtml(p.t)}</span>${sub}` +
        `</span></a></li>`
      );
    })
    .join("");

  return (
    `<ul id="live-search-results-list" class="custom-live-search__list" role="listbox">${rows}</ul>` +
    `<a class="custom-live-search__more" href="${psRoot()}search?q=${encodeURIComponent(query)}&type=product">Visa alla träffar</a>`
  );
}

// ---------------------------------------------------------------------------

class SearchForm extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.resetButton = this.querySelector('button[type="reset"]');

    this.resultsBox = document.createElement("div");
    this.resultsBox.className = "custom-live-search";
    this.resultsBox.setAttribute("aria-live", "polite");
    this.resultsBox.style.display = "none";
    document.body.appendChild(this.resultsBox);

    this.updateResultsBoxPosition = () => {
      if (this.input) {
        const rect = this.input.getBoundingClientRect();
        this.resultsBox.style.left = rect.left + "px";
        this.resultsBox.style.top = rect.bottom + "px";
        this.resultsBox.style.width = rect.width + "px";
      }
    };

    window.addEventListener(
      "scroll",
      () => {
        if (this.resultsBox.style.display === "block") this.updateResultsBoxPosition();
      },
      { passive: true }
    );
    window.addEventListener("resize", () => {
      if (this.resultsBox.style.display === "block") this.updateResultsBoxPosition();
    });

    this.abortController = null;

    if (this.input && this.input.form) {
      this.input.form.addEventListener("reset", this.onFormReset.bind(this));

      this.input.addEventListener(
        "input",
        debounce(() => this.onChange(), PS_DEBOUNCE_MS).bind(this)
      );

      // Förladda indexet så första sökningen är blixtsnabb
      this.input.addEventListener("focus", () => {
        psLoadIndex().catch(() => {});
        if (this.resultsBox.innerHTML.trim() && this.currentQuery().length >= PS_MIN_CHARS) {
          this.resultsBox.style.display = "block";
          this.updateResultsBoxPosition();
        }
      });

      document.addEventListener("click", (event) => {
        if (!this.contains(event.target)) this.closeResults();
      });
    }
  }

  toggleResetButton() {
    if (!this.resetButton || !this.input) return;
    const resetIsHidden = this.resetButton.classList.contains("hidden");
    if (this.input.value.length > 0 && resetIsHidden) {
      this.resetButton.classList.remove("hidden");
    } else if (this.input.value.length === 0 && !resetIsHidden) {
      this.resetButton.classList.add("hidden");
    }
  }

  currentQuery() {
    return this.input ? this.input.value.trim() : "";
  }

  onChange() {
    this.toggleResetButton();

    const query = this.currentQuery();

    if (query.length < PS_MIN_CHARS) {
      this.closeResults(true);
      return;
    }

    psLoadIndex()
      .then((products) => {
        // Rendera bara om frågan fortfarande är aktuell
        if (this.currentQuery() !== query) return;
        this.showResults(psRenderRows(psMatch(products, query), query));
      })
      .catch(() => {
        // Indexet gick inte att ladda - fall tillbaka på serversökningen
        this.serverSearch(query);
      });
  }

  // Gamla flödet: server-renderad minisektion via /search (fulltext).
  serverSearch(query) {
    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();

    const url =
      `${psRoot()}search?q=${encodeURIComponent(query)}` +
      `&section_id=${PS_FALLBACK_SECTION_ID}` +
      `&type=product` +
      `&options[prefix]=last`;

    fetch(url, { signal: this.abortController.signal })
      .then((r) => r.text())
      .then((text) => {
        const doc = new DOMParser().parseFromString(text, "text/html");
        const section = doc.getElementById("shopify-section-" + PS_FALLBACK_SECTION_ID);
        const markup = section ? section.innerHTML.trim() : "";
        if (markup && this.currentQuery() === query) this.showResults(markup);
      })
      .catch(() => { /* behåll det som visas */ });
  }

  showResults(markup) {
    this.resultsBox.innerHTML = markup;
    this.resultsBox.style.display = "block";
    this.updateResultsBoxPosition();
  }

  closeResults(clear = false) {
    this.resultsBox.style.display = "none";
    if (clear) this.resultsBox.innerHTML = "";
  }

  shouldResetForm() {
    return !document.querySelector('[aria-selected="true"] a');
  }

  onFormReset(event) {
    event.preventDefault();
    if (this.shouldResetForm()) {
      this.input.value = "";
      this.input.focus();
      this.toggleResetButton();
      this.closeResults(true);
    }
  }
}

customElements.define("search-form", SearchForm);
